import React from 'react';

/**
 * Reusable decorative SVG motifs for the "warm editorial" menu aesthetic.
 *
 * All motifs draw with `currentColor`, so they inherit the surrounding text
 * color (petrol/teal by default, or whatever a shop's deco-*.css sets). They
 * are purely decorative — marked aria-hidden so screen readers skip them.
 *
 * Generic + reusable on purpose: menu categories are dynamic (fetched from the
 * API), so the illustrated warmth comes from a small shared set of botanical
 * marks rather than per-category artwork.
 */

interface OrnamentProps {
  className?: string;
}

/** A small two-leaf sprig — used as an inline mark beside eyebrows/titles. */
export const LeafMark: React.FC<OrnamentProps> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M10 18V7"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <path
      d="M10 9c0-2.3 1.6-4.4 4-5-.2 2.6-1.6 4.6-4 5Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path
      d="M10 12c0-2.3-1.6-4.4-4-5 .2 2.6 1.6 4.6 4 5Z"
      fill="currentColor"
      opacity="0.55"
    />
    <circle cx="10" cy="4.5" r="1.4" fill="currentColor" />
  </svg>
);

/** A four-point sparkle — tiny accent for cards/eyebrows. */
export const Sparkle: React.FC<OrnamentProps> = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7 0c.5 3.7 2.3 5.5 6 6-3.7.5-5.5 2.3-6 6-.5-3.7-2.3-5.5-6-6 3.7-.5 5.5-2.3 6-6Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * A horizontal divider with a centered botanical sprig — sits under section
 * titles to give the printed-menu / editorial feel.
 */
export const SprigDivider: React.FC<OrnamentProps> = ({ className }) => (
  <svg
    className={className}
    width="148"
    height="22"
    viewBox="0 0 148 22"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    {/* tapering rules left & right */}
    <line x1="6" y1="11" x2="52" y2="11" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.35" />
    <line x1="96" y1="11" x2="142" y2="11" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.35" />
    {/* left leaf pair */}
    <path d="M64 11c-3.4 0-6.2-1.7-7.6-4.5 3.1-.5 6 1 7.6 4.5Z" fill="currentColor" opacity="0.6" />
    <path d="M64 11c-2.8 1.8-6 2-8.8.6 2.2-2 5-2.4 8.8-.6Z" fill="currentColor" opacity="0.4" />
    {/* right leaf pair (mirror) */}
    <path d="M84 11c3.4 0 6.2-1.7 7.6-4.5-3.1-.5-6 1-7.6 4.5Z" fill="currentColor" opacity="0.6" />
    <path d="M84 11c2.8 1.8 6 2 8.8.6-2.2-2-5-2.4-8.8-.6Z" fill="currentColor" opacity="0.4" />
    {/* center diamond */}
    <path d="M74 5l3 6-3 6-3-6 3-6Z" fill="currentColor" />
  </svg>
);
